import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { EditableDetectedItem } from '../types/foodRecognition';
import {
  syncInventoryToCloud,
  fetchInventoryFromCloud,
  syncItemToCloud,
  deleteItemFromCloud,
  mergeInventories,
  clearCloudInventory,
} from '../services/syncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { ReceiptMetadata } from '../types/foodRecognition';
import { normalizeIngredientName } from '../services/groceryListService';
import { detectCategoryFromName, detectCategoryConfident } from '../utils/inventoryUtils';
import { PantryLocation, withLocation, inferLocation, inferLocationConfident } from '../utils/pantryLocation';
import { decidePantryAdd, type PantryAddSkip } from '../utils/pantryAddDecision';
import { resolveIngredient } from '../utils/resolveIngredient';
import { entryFor } from '../utils/ingredientVocabulary';
import {
  getInventoryMatchesForRecipe,
  ingredientMatchKey,
} from '../services/inventoryMatchService';
import { findDuplicate, planDuplicateMerge, primaryKey } from '../utils/pantryDuplicates';
import { shelfLifeExpiryDate } from '../services/shelfLifeService';
import type { PantryExpiryDate } from '../utils/pantryExpiry';
import { LEFTOVER_CATEGORY, isCookedLeftover } from '../utils/pantryLocation';
import { ShelfLifeBucket, toShelfLifeBucket } from '../types/foodRecognition';
import { normalizePantryQuantity } from '../utils/pantryQuantity';
import { RecipeIngredient } from '../types/recipe';
import { RootState } from './store';
import { saveDeductionSnapshot, clearDeductionSnapshot, DeductionItem } from './cookingSessionSlice';

export interface InventoryItem {
  id: string;
  name: string;
  /**
   * The food this row is, when its name cannot say so. Written only when the
   * recipe line was a more specific kind of the food than the cleaned name —
   * "1 pound de bœuf haché" is stored as "bœuf" and must still read back as
   * ground beef (#601). Absent on every other row; `buildPantryIndex` resolves
   * the name instead.
   */
  canonicalId?: string;
  /**
   * The row's name in both languages, when its source gave both (a Lufa order
   * does). Read only by `pantryDisplayName`, and only while `name` is one of
   * the two. Never rewritten when a merge or a rename changes `name`, because a
   * merge write to Firestore cannot clear it.
   */
  names?: { en: string; fr: string };
  category: string;
  confidence?: number;
  dateAdded: string; // ISO date string
  lastModified?: string; // ISO date string - tracks when item was last modified (for sync conflict resolution)
  expiryDate?: PantryExpiryDate;
  /**
   * The package is open or the item is cut. Shelf life depends on it far more
   * than on anything else the pantry knows: sealed tofu keeps six weeks, opened
   * tofu four days.
   */
  opened?: boolean;
  openedAt?: string; // ISO date the item was marked opened
  imageUri?: string; // Optional image of the item (not persisted to save space)
  isExpiringSoon?: boolean; // Computed property for items expiring within 7 days

  // Item status tracking
  status?: 'active' | 'eaten' | 'discarded'; // Track item lifecycle (undefined = 'active' for backwards compatibility)
  eatenAt?: string; // ISO date when marked as eaten
  consumedBeforeExpiry?: boolean; // Waste reduction metric

  // Quantity tracking (Phase 3+4 - Hybrid system)
  quantity?: number; // Amount of item. For count-based: whole numbers (1, 2, 3). For measured: can be decimal (2.5 cups)
  unit?: string; // Unit of measurement. undefined = count-based (default). Examples: 'cups', 'g', 'ml', 'tablespoons'

  // Receipt-specific fields (optional)
  purchasePrice?: number;
  purchaseDate?: string; // ISO date string
  purchaseStore?: string;
  source?: 'fridge' | 'receipt' | 'lufa-delivery' | 'demo' | 'leftover'; // Track where the item came from
  /**
   * Frozen leftovers (#663) keep a link back to the recipe so the planner can
   * still show the photo and open the detail sheet. Absent on every other row —
   * a bag of peas is not a recipe.
   */
  leftoverRecipeId?: string;
  /** ISO date the dish was cooked, for "congelé le ..." on the picker row. */
  leftoverCookedAt?: string;
  deliveryDate?: string; // ISO date for Lufa delivery items

  // Email-import batch (a forwarded Lufa order). `reviewed === undefined` means
  // reviewed: everything added through the app is, only an import writes false.
  importId?: string; // e.g. "lufa-26935138" — one per order, shared by its items
  importedAt?: string; // ISO timestamp of the import
  reviewed?: boolean;

  // Pantry redesign: physical storage space (fridge/freezer/counter/pantry).
  // Inferred from category+name when absent (legacy items, cloud items).
  location?: PantryLocation;

  // True when neither the category nor the zone could actually be determined
  // and the row is only sitting somewhere by default. Surfaces in the pantry's
  // "to sort" tray so the user places it, instead of it being silently filed on
  // the shelf — which is what 'Other' used to mean.
  //
  // Distinct from the freshness `review` state (pantrySelectors), which asks
  // "is this still good"; this one asks "what is this, and where does it live".
  needsSorting?: boolean;
}

interface SyncStatus {
  isSyncing: boolean;
  lastSyncTime: string | null;
  error: string | null;
}

interface InventoryState {
  items: InventoryItem[];
  lastUpdated: string | null;
  lastCleared: string | null;
  sync: SyncStatus;
}

const initialState: InventoryState = {
  items: [],
  lastUpdated: null,
  lastCleared: null,
  sync: {
    isSyncing: false,
    lastSyncTime: null,
    error: null,
  },
};

// Migration helper: Ensure all items have a status field
// Guards against null/undefined items from corrupted persisted state
const migrateItemStatus = (item: InventoryItem): InventoryItem => {
  if (!item) return item;
  if (!item.status) {
    return { ...item, status: 'active' };
  }
  return item;
};

/**
 * Which rows this device still has a claim on.
 *
 * The upload writes with `merge: true` and never deletes, so anything it sends
 * is re-created in the cloud. Sending the whole local list therefore resurrects
 * every item another device deleted: clear the pantry on the laptop, open the
 * phone, and the phone puts all of it back. That is not hypothetical — it is
 * how a 236-row pantry came back three minutes after being deleted.
 *
 * A row untouched since this device last synced is the cloud's business. If it
 * is gone up there, someone deleted it and this device has nothing newer to
 * say. Only rows changed since then are ours to push.
 *
 * Two deliberate escape hatches, both erring toward keeping data:
 *   - never synced (`lastSyncTime` null) — push everything, nothing to compare
 *   - no `lastModified` — push it; we cannot reason about it, and losing a row
 *     is worse than resurrecting one
 *
 * What this does NOT solve: an edit on one device racing a delete on another.
 * Without tombstones there is no way to order those, and the edit wins.
 */
export function itemsToUpload(
  items: InventoryItem[],
  lastSyncTime: string | null | undefined
): InventoryItem[] {
  if (!lastSyncTime) return items;
  return items.filter((item) => !item.lastModified || item.lastModified > lastSyncTime);
}

/**
 * Upload locally-changed inventory to cloud
 */
export const uploadInventoryToCloud = createAsyncThunk(
  'inventory/uploadToCloud',
  async (userId: string, { getState, rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }
      const fullState = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
      const householdId = fullState.auth?.householdId ?? null;
      const outgoing = itemsToUpload(
        fullState.inventory.items,
        fullState.inventory.sync?.lastSyncTime
      );
      if (outgoing.length > 0) {
        await syncInventoryToCloud(userId, outgoing, householdId);
      }
      return { syncTime: new Date().toISOString() };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Upload failed');
    }
  }
);

/**
 * Download inventory from cloud and merge with local
 */
export const downloadInventoryFromCloud = createAsyncThunk(
  'inventory/downloadFromCloud',
  async (userId: string, { getState, rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }
      const fullState = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
      const householdId = fullState.auth?.householdId ?? null;
      const cloudItems = await fetchInventoryFromCloud(userId, householdId);
      const mergedItems = mergeInventories(fullState.inventory.items, cloudItems);
      return { items: mergedItems, syncTime: new Date().toISOString() };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Download failed');
    }
  }
);

/**
 * Full sync - upload local changes and download cloud changes
 */
export const fullSync = createAsyncThunk(
  'inventory/fullSync',
  async (userId: string, { dispatch, rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }
      // Download and merge first
      await dispatch(downloadInventoryFromCloud(userId)).unwrap();
      // Then upload merged state
      await dispatch(uploadInventoryToCloud(userId)).unwrap();
      return { syncTime: new Date().toISOString() };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Sync failed');
    }
  }
);

/**
 * Add items and sync to cloud
 */
export const addItemsAndSync = createAsyncThunk(
  'inventory/addItemsAndSync',
  async (
    { items, imageUri, receiptMetadata, userId }: {
      items: EditableDetectedItem[];
      imageUri?: string;
      receiptMetadata?: ReceiptMetadata;
      userId?: string;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const stateBefore = getState() as { inventory: InventoryState };
      const countBefore = stateBefore.inventory.items.length;

      // Where the user has already said this food lives. Passing it here is
      // what closes the loop with the capture review: correct the shrimp once,
      // and the next receipt shelves them in the freezer on its own.
      const learnedZones = (stateBefore as { pantryLearning?: { zones?: Record<string, PantryLocation> } })
        .pantryLearning?.zones;

      // Update local state first (optimistic update)
      dispatch(inventorySlice.actions.addItems({ items, imageUri, receiptMetadata, learnedZones }));

      // Sync to cloud if user is authenticated
      if (userId && isFirebaseEnabled()) {
        const state = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
        const householdId = state.auth?.householdId ?? null;
        // Only what the reducer actually appended — it drops duplicates, so
        // slicing by the requested count would re-sync pre-existing rows.
        const newItems = state.inventory.items.slice(countBefore);

        // Sync each new item to cloud
        await Promise.all(newItems.map(item => syncItemToCloud(userId, item, householdId)));
      }

      return { itemsCount: items.length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add items');
    }
  }
);

/**
 * Mark an email-imported delivery as reviewed and push the change to the cloud.
 *
 * The rows were written by the server, so the cloud copy still says
 * `reviewed: false`. Only syncing the ones we just touched keeps the write
 * small; the timestamp bump in the reducer is what makes them win the merge.
 */
export const markImportReviewedAndSync = createAsyncThunk(
  'inventory/markImportReviewedAndSync',
  async (
    { importId, userId }: { importId: string; userId?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(inventorySlice.actions.markImportReviewed(importId));

      if (userId && isFirebaseEnabled()) {
        const state = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
        const householdId = state.auth?.householdId ?? null;
        const batch = state.inventory.items.filter(item => item?.importId === importId);
        await Promise.all(batch.map(item => syncItemToCloud(userId, item, householdId)));
      }

      return { importId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark import reviewed');
    }
  }
);

/**
 * Update item and sync to cloud
 */
export const updateItemAndSync = createAsyncThunk(
  'inventory/updateItemAndSync',
  async (
    { id, updates, userId }: { id: string; updates: Partial<InventoryItem>; userId?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      // Update local state first (optimistic update)
      dispatch(inventorySlice.actions.updateItem({ id, updates }));

      // Sync to cloud if user is authenticated
      if (userId && isFirebaseEnabled()) {
        const state = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
        const householdId = state.auth?.householdId ?? null;
        const updatedItem = state.inventory.items.find(item => item.id === id);

        if (updatedItem) {
          await syncItemToCloud(userId, updatedItem, householdId);
        }
      }

      return { id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update item');
    }
  }
);

/**
 * Freeze portions of a cooked dish, and push the row to the cloud (#663).
 *
 * `id` is generated by the caller so the planner can link the slot it just
 * filled back to this row without waiting for a round trip.
 */
export const addLeftoverToFreezerAndSync = createAsyncThunk(
  'inventory/addLeftoverToFreezerAndSync',
  async (
    args: {
      id: string;
      name: string;
      recipeId?: string;
      portions: number;
      cookedAt: string;
      expiryDate?: PantryExpiryDate;
      userId?: string;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    const { userId, ...payload } = args;
    try {
      dispatch(inventorySlice.actions.addLeftoverToFreezer(payload));

      if (userId && isFirebaseEnabled()) {
        const state = getState() as {
          inventory: InventoryState;
          auth?: { householdId?: string | null };
        };
        const item = state.inventory.items.find((i) => i.id === payload.id);
        if (item) {
          await syncItemToCloud(userId, item, state.auth?.householdId ?? null);
        }
      }
      return { id: payload.id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to freeze leftovers');
    }
  }
);

/**
 * Take a serving out of the freezer and push the new count to the cloud.
 */
export const consumeLeftoverPortionAndSync = createAsyncThunk(
  'inventory/consumeLeftoverPortionAndSync',
  async (
    { id, portions, userId }: { id: string; portions?: number; userId?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(inventorySlice.actions.consumeLeftoverPortion({ id, portions }));

      if (userId && isFirebaseEnabled()) {
        const state = getState() as {
          inventory: InventoryState;
          auth?: { householdId?: string | null };
        };
        const item = state.inventory.items.find((i) => i.id === id);
        if (item) {
          await syncItemToCloud(userId, item, state.auth?.householdId ?? null);
        }
      }
      return { id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to consume leftover');
    }
  }
);

/**
 * Remove item and sync to cloud
 */
export const removeItemAndSync = createAsyncThunk(
  'inventory/removeItemAndSync',
  async ({ itemId, userId }: { itemId: string; userId?: string }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Update local state first (optimistic update)
      dispatch(inventorySlice.actions.removeItem(itemId));

      // Sync to cloud if user is authenticated
      if (userId && isFirebaseEnabled()) {
        const state = getState() as { auth?: { householdId?: string | null } };
        const householdId = state.auth?.householdId ?? null;
        await deleteItemFromCloud(userId, itemId, householdId);
      }

      return { itemId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to remove item');
    }
  }
);

/**
 * Merge duplicate pantry rows and sync to cloud.
 *
 * Destructive: the absorbed rows are deleted remotely as well as locally, so
 * this is only ever dispatched from an explicit, previewed user confirmation.
 */
export const mergeDuplicatesAndSync = createAsyncThunk(
  'inventory/mergeDuplicatesAndSync',
  async (
    { groups, userId }: { groups: string[][]; userId?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const stateBefore = getState() as { inventory: InventoryState };

      // Resolve what will survive and what will go *before* mutating state —
      // afterwards the absorbed rows are gone and their ids unrecoverable.
      const plans = groups
        .map(ids =>
          ids
            .map(id => stateBefore.inventory.items.find(item => item.id === id))
            .filter((item): item is InventoryItem => !!item)
        )
        .filter(members => members.length > 1)
        .map(planDuplicateMerge);

      if (plans.length === 0) return { groups: 0, itemsRemoved: 0 };

      // Update local state first (optimistic update)
      dispatch(inventorySlice.actions.mergeDuplicates({ groups }));

      const removedIds = plans.flatMap(plan => plan.absorbed.map(item => item.id));

      if (userId && isFirebaseEnabled()) {
        const stateAfter = getState() as {
          inventory: InventoryState;
          auth?: { householdId?: string | null };
        };
        const householdId = stateAfter.auth?.householdId ?? null;

        await Promise.all(
          plans.map(plan => {
            const survivor = stateAfter.inventory.items.find(item => item.id === plan.survivor.id);
            return survivor
              ? syncItemToCloud(userId, survivor, householdId)
              : Promise.resolve();
          })
        );

        await Promise.all(
          removedIds.map(itemId => deleteItemFromCloud(userId, itemId, householdId))
        );
      }

      return { groups: plans.length, itemsRemoved: removedIds.length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to merge duplicates');
    }
  }
);

/**
 * Mark item as eaten and sync to cloud
 */
export const markAsEatenAndSync = createAsyncThunk(
  'inventory/markAsEatenAndSync',
  async ({ itemId, userId }: { itemId: string; userId?: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Update local state first (optimistic update)
      dispatch(inventorySlice.actions.markAsEaten(itemId));

      // Sync to cloud if user is authenticated
      if (userId && isFirebaseEnabled()) {
        const state = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
        const householdId = state.auth?.householdId ?? null;
        const updatedItem = state.inventory.items.find(item => item.id === itemId);

        if (updatedItem) {
          await syncItemToCloud(userId, updatedItem, householdId);
        }
      }

      return { itemId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark item as eaten');
    }
  }
);

/**
 * Mark multiple items as eaten and sync to cloud
 */
export const markMultipleAsEatenAndSync = createAsyncThunk(
  'inventory/markMultipleAsEatenAndSync',
  async ({ itemIds, userId }: { itemIds: string[]; userId?: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Update local state first (optimistic update)
      dispatch(inventorySlice.actions.markMultipleAsEaten(itemIds));

      // Sync to cloud if user is authenticated
      if (userId && isFirebaseEnabled()) {
        const state = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
        const householdId = state.auth?.householdId ?? null;

        // Sync all updated items
        const syncPromises = itemIds.map(itemId => {
          const updatedItem = state.inventory.items.find(item => item.id === itemId);
          if (updatedItem) {
            return syncItemToCloud(userId, updatedItem, householdId);
          }
          return Promise.resolve();
        });

        await Promise.all(syncPromises);
      }

      return { itemIds };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark items as eaten');
    }
  }
);

/**
 * Add ingredients to pantry and sync to cloud
 * Wraps the addItemsToPantry reducer with cloud sync (same pattern as addItemsAndSync)
 */
export const addItemsToPantryAndSync = createAsyncThunk(
  'inventory/addItemsToPantryAndSync',
  async (
    { ingredientNames, userId }: { ingredientNames: string[]; userId?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const stateBefore = getState() as { inventory: InventoryState };
      const countBefore = stateBefore.inventory.items.length;

      // Update local state first (optimistic update)
      const learnedZones = (stateBefore as { pantryLearning?: { zones?: Record<string, PantryLocation> } })
        .pantryLearning?.zones;
      const excludedStaples =
        (stateBefore as { userPreferences?: { excludedStaples?: string[] } }).userPreferences
          ?.excludedStaples ?? [];

      // Why each refused name was refused, decided exactly as the reducer will
      // (same function, same order, the batch's own adds counted as it goes), so
      // the caller can say "a staple" or "already there as X" instead of one
      // blanket « déjà au garde-manger » (#796).
      const skipped: Array<{ name: string } & PantryAddSkip> = [];
      const seen = [...stateBefore.inventory.items];
      for (const name of ingredientNames) {
        const decision = decidePantryAdd(name, seen, excludedStaples);
        if (decision.kind === 'skip') skipped.push({ name, ...decision });
        else seen.push({ id: '', name: decision.name, status: 'active' } as InventoryItem);
      }

      dispatch(inventorySlice.actions.addItemsToPantry({ ingredientNames, learnedZones, excludedStaples }));

      // The reducer drops names that collide with an item already in the pantry.
      // Report how many actually landed so the caller does not announce success
      // for a write that never happened.
      const stateAfter = getState() as { inventory: InventoryState; auth?: { householdId?: string | null } };
      const countAfter = stateAfter.inventory.items.length;
      const addedCount = countAfter - countBefore;

      // Sync new items to cloud if user is authenticated
      if (userId && isFirebaseEnabled()) {
        const householdId = stateAfter.auth?.householdId ?? null;
        const newItems = stateAfter.inventory.items.slice(countBefore, countAfter);

        if (newItems.length > 0) {
          await Promise.all(newItems.map(item => syncItemToCloud(userId, item, householdId)));
        }
      }

      return { ingredientNames, addedCount, skipped };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add pantry items');
    }
  }
);

/**
 * Clear all inventory items and sync to cloud
 */
export const clearInventoryAndSync = createAsyncThunk(
  'inventory/clearInventoryAndSync',
  async ({ userId }: { userId?: string }, { dispatch, getState, rejectWithValue }) => {
    // A paired user's pantry lives at households/{householdId}/inventory, not
    // users/{uid}/inventory. This thunk was the only one in the slice that did
    // not resolve the scope, so a household clear deleted nothing, reported
    // success, and the listener put every item back once the syncFromCloud
    // grace period lapsed. Every sibling thunk reads it the same way.
    const fullState = getState() as {
      inventory: InventoryState;
      auth?: { householdId?: string | null };
    };
    const householdId = fullState.auth?.householdId ?? null;
    const localActiveCount = fullState.inventory.items.filter(
      (item) => !item.status || item.status === 'active'
    ).length;

    try {
      // Clear cloud first so the real-time listener gets the empty state
      if (userId && isFirebaseEnabled()) {
        const clearedCount = await clearCloudInventory(userId, householdId);

        // Deleting nothing while the shelves are full means we were pointed at
        // the wrong collection. The pantry still empties locally, so say it is
        // local-only rather than claiming a cloud clear that did not happen.
        if (clearedCount === 0 && localActiveCount > 0) {
          dispatch(inventorySlice.actions.clearInventory());
          return rejectWithValue(
            `Cleared 0 cloud items while ${localActiveCount} were on the shelves`
          );
        }
      }

      // Then clear local state
      dispatch(inventorySlice.actions.clearInventory());

      return {};
    } catch (error: any) {
      // Still clear local state even if cloud fails
      dispatch(inventorySlice.actions.clearInventory());
      return rejectWithValue(error.message || 'Failed to clear cloud inventory');
    }
  }
);

/**
 * Interface for the return value of consumeItemsForCooking
 */
export interface DeductionSummary {
  ingredientName: string;
  pantryItemName: string;
  previousQuantity: number | undefined;
  previousUnit: string | undefined;
  newQuantity: number | undefined;
  newUnit: string | undefined;
}

/**
 * Consume items from pantry for cooking a recipe at specified servings.
 * Deducts quantities from matching inventory items and saves pre-deduction snapshot for undo.
 * Skips spices and condiments per design rules.
 */
export const consumeItemsForCooking = createAsyncThunk(
  'inventory/consumeItemsForCooking',
  async (
    {
      extendedIngredients,
      servings,
      plannedServings,
      userId,
    }: {
      extendedIngredients: RecipeIngredient[];
      servings: number;
      plannedServings: number;
      userId?: string;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const inventoryItems = state.inventory.items;

      // Get matches between recipe ingredients and inventory
      const matchesMap = getInventoryMatchesForRecipe(
        extendedIngredients,
        inventoryItems
      );

      const deductionSummaries: DeductionSummary[] = [];
      const deductionSnapshot: DeductionItem[] = [];

      // Process each ingredient
      for (const ingredient of extendedIngredients) {
        // Same key the map was built with — see ingredientMatchKey (#601).
        const ingredientName = ingredientMatchKey(ingredient);
        const match = matchesMap.get(ingredientName);

        // Skip if no match found
        if (!match || !match.matched) {
          continue;
        }

        // Skip spices and condiments
        const category = detectCategoryFromName(ingredientName);
        if (category === 'Spices' || category === 'Condiments') {
          continue;
        }

        // Find the actual inventory item by matching name
        const pantryItem = inventoryItems.find(
          item =>
            item.name &&
            normalizeIngredientName(item.name) ===
              normalizeIngredientName(match.inventoryItemName || '')
        );

        if (!pantryItem) {
          continue;
        }

        // Calculate scaled amount for actual servings
        const rawAmount = typeof ingredient.amount === 'number' ? ingredient.amount : parseFloat(String(ingredient.amount));
        if (!rawAmount || isNaN(rawAmount) || isNaN(servings) || isNaN(plannedServings) || plannedServings === 0) {
          continue;
        }
        const scaledAmount = rawAmount * (servings / plannedServings);

        // Save pre-deduction state for undo snapshot
        deductionSnapshot.push({
          inventoryItemId: pantryItem.id,
          name: pantryItem.name,
          previousQuantity: pantryItem.quantity,
          previousUnit: pantryItem.unit,
          previousStatus: pantryItem.status || 'active',
        });

        let newQuantity: number | undefined = pantryItem.quantity;
        let newUnit: string | undefined = pantryItem.unit;

        // Deduction logic
        if (pantryItem.quantity === undefined) {
          // Count-based without quantity: mark as eaten
          dispatch(markAsEatenAndSync({ itemId: pantryItem.id, userId }));
          newQuantity = undefined;
        } else if (pantryItem.quantity <= scaledAmount) {
          // Fully consumed: mark as eaten
          dispatch(markAsEatenAndSync({ itemId: pantryItem.id, userId }));
          newQuantity = undefined;
        } else {
          // Partial consumption: deduct from quantity
          const remainingQuantity = pantryItem.quantity - scaledAmount;
          dispatch(
            updateItemAndSync({
              id: pantryItem.id,
              updates: { quantity: remainingQuantity },
              userId,
            })
          );
          newQuantity = remainingQuantity;
        }

        // Add to summary
        deductionSummaries.push({
          ingredientName,
          pantryItemName: pantryItem.name,
          previousQuantity: pantryItem.quantity,
          previousUnit: pantryItem.unit,
          newQuantity,
          newUnit,
        });
      }

      // Save deduction snapshot for undo
      if (deductionSnapshot.length > 0) {
        dispatch(saveDeductionSnapshot(deductionSnapshot));
      }

      return deductionSummaries;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to consume items for cooking'
      );
    }
  }
);

/**
 * Restore inventory to pre-deduction state (undo cooking deduction).
 * Reads deductionSnapshot from cookingSession state and restores items.
 */
export const restoreDeductionSnapshot = createAsyncThunk(
  'inventory/restoreDeductionSnapshot',
  async ({ userId }: { userId?: string }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const deductionSnapshot = state.cookingSession.deductionSnapshot;

      // Early return if no snapshot
      if (!deductionSnapshot || deductionSnapshot.length === 0) {
        return { restored: 0 };
      }

      let restoredCount = 0;

      // Restore each item in snapshot
      for (const item of deductionSnapshot) {
        if (item.previousStatus === 'eaten') {
          // Item was already eaten before deduction — restore status to active
          dispatch(
            updateItemAndSync({
              id: item.inventoryItemId,
              updates: {
                status: 'active',
                eatenAt: undefined,
                quantity: item.previousQuantity,
                unit: item.previousUnit,
              },
              userId,
            })
          );
        } else if (item.previousQuantity !== undefined) {
          // Item had a quantity — restore it
          dispatch(
            updateItemAndSync({
              id: item.inventoryItemId,
              updates: {
                quantity: item.previousQuantity,
                unit: item.previousUnit,
              },
              userId,
            })
          );
        } else if (item.previousStatus === 'active') {
          // Count-based item — restore active status
          dispatch(
            updateItemAndSync({
              id: item.inventoryItemId,
              updates: { status: 'active' },
              userId,
            })
          );
        }
        restoredCount++;
      }

      // Clear the snapshot after restoring
      dispatch(clearDeductionSnapshot());

      return { restored: restoredCount };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to restore deduction snapshot'
      );
    }
  }
);

/**
 * Validate that an inventory item has the minimum required fields.
 * Guards against corrupted persisted state or malformed cloud data.
 */
const isValidInventoryItem = (item: any): item is InventoryItem =>
  item != null && typeof item === 'object' && typeof item.name === 'string' && item.name.trim() !== '';

/**
 * The canonical id to store on a row written from `raw`, or null.
 *
 * The cleaner keeps the food and drops the rest, and sometimes the rest was
 * the food: "1 pound de bœuf haché" becomes "bœuf", "Whole chicken" becomes
 * "chicken". The recipe screen resolves the line to ground beef and the row to
 * beef, so the item the user just shelved reads back as missing (#601). Only a
 * more specific *kind* is kept — never a derived form: "Zeste de 1 citron" is
 * a lemon on the shelf, not lemon zest.
 */
function specificIdOf(raw: string, name: string): string | null {
  const lineId = resolveIngredient(raw).canonicalId;
  if (!lineId) return null;
  const nameId = resolveIngredient(name).canonicalId;
  return nameId && lineId !== nameId && entryFor(lineId)?.parent === nameId ? lineId : null;
}

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    addItems: (
      state,
      action: PayloadAction<{
        items: EditableDetectedItem[];
        imageUri?: string;
        receiptMetadata?: ReceiptMetadata;
        learnedZones?: Record<string, PantryLocation>;
      }>
    ) => {
      const { items: rawItems, imageUri, receiptMetadata, learnedZones } = action.payload;
      // Filter out items without a valid name before processing
      const items = rawItems.filter(i => i && typeof i.name === 'string' && i.name.trim() !== '');
      const now = new Date().toISOString();

      const newItems: InventoryItem[] = items.map((item) => {
        let expiryDate = undefined;

        // The model's bucket is a hint, not the answer: shelfLifeService looks
        // the food up by name first and only falls back to what the scan said.
        // Legacy three-value buckets from older responses map across on the way in.
        const rawBucket = (item as any).expiry_category || (item as any).shelf_life_estimate;
        const hint: ShelfLifeBucket | undefined = toShelfLifeBucket(rawBucket);
        const itemCategory = (item.category as string) || 'Other';

        // One zone for the whole row. This used to be worked out twice, from
        // two different category values, and through `inferLocation` — the
        // wrapper that throws away "I could not tell". So a row nobody could
        // place was filed on the shelf *confidently*, never reached the "to
        // sort" tray, and had its expiry computed for a shelf it did not
        // belong on. `addItemsToPantry` has done it this way since #617.
        //
        // An explicit location outranks the rules: it is either the receipt's
        // own "Frozen Foods", the zone the user photographed, or the choice
        // they just made in the capture review. None of those are guesses.
        const zone = item.location
          ? { location: item.location, confident: true }
          : inferLocationConfident(itemCategory, item.name, true, learnedZones?.[primaryKey(item.name)]);

        if (hint || item.name) {
          expiryDate = shelfLifeExpiryDate(
            {
              name: item.name,
              category: itemCategory,
              location: zone.location,
              opened: (item as any).opened,
              condition: item.visible_condition,
              hint,
            },
            new Date(),
            normalizeIngredientName(item.name)
          );
        }
        // Legacy: Convert AI response days_until_expiry to actual date structure
        else if (item.expiry_date && typeof (item.expiry_date as any).days_until_expiry === 'number') {
          const today = new Date();
          const daysUntilExpiry = (item.expiry_date as any).days_until_expiry;

          // If expired (negative days), set to today
          const expiryDateObj = new Date(today);
          if (daysUntilExpiry <= 0) {
            // Already expired - set to today
            expiryDateObj.setDate(today.getDate());
          } else {
            // Add the days to today's date
            expiryDateObj.setDate(today.getDate() + daysUntilExpiry);
          }

          expiryDate = {
            day: expiryDateObj.getDate(),
            month: expiryDateObj.getMonth() + 1, // JavaScript months are 0-based
            year: expiryDateObj.getFullYear(),
            confidence: item.expiry_date.confidence,
            explanation: item.expiry_date.explanation
          };
        } else if (item.expiry_date && (item.expiry_date as any).day) {
          // Handle legacy format (already in day/month/year format)
          expiryDate = item.expiry_date as any;
        }

        // Determine source and extract receipt-specific fields
        const source: 'fridge' | 'receipt' = receiptMetadata ? 'receipt' : 'fridge';

        // Extract quantity from AI response (Phase 3).
        // Receipt scans pre-parse quantity + unit (e.g. 1.5 kg kimchi, 3 lb kiwis):
        // prefer those so measured amounts survive into the pantry. Count-based
        // items stay whole numbers; measured items keep decimals and their unit.
        // Shared with the email-import handler via pantryQuantity.
        const { quantity, unit } = normalizePantryQuantity({
          quantity: item.quantity,
          unit: item.unit,
          quantityEstimate: item.quantity_estimate,
        });

        return {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: item.name,
          ...(item.names ? { names: item.names } : {}),
          category: item.category,
          confidence: item.confidence,
          dateAdded: (item as any).dateAdded ?? now,
          lastModified: now, // Track modification time for sync
          expiryDate,
          imageUri,
          isExpiringSoon: expiryDate ? isExpiringSoon(expiryDate) : false,
          status: 'active' as const, // New items are always active
          quantity, // Phase 3: Store AI-detected quantity
          ...(unit ? { unit } : {}), // Measured items (kg/lb/L) keep their unit
          location: zone.location,
          // Nobody could place it: let the "to sort" tray ask, rather than
          // pretending the shelf was an answer.
          ...(zone.confident ? {} : { needsSorting: true }),

          // Receipt-specific fields (if present).
          // purchasePrice is deliberately NOT stored from receipt scans: the
          // eval harness measures only ~40% of extracted prices as correct,
          // because a warehouse receipt's price column is visually sheared by
          // the twist in the paper and the model pairs names with neighbouring
          // rows' prices. Wrong prices are worse than none. Re-enable once
          // `npm run eval:receipt` reports price-exact near 100%.
          source: (item as any).source ?? source,
          ...(receiptMetadata?.purchase_date && { purchaseDate: receiptMetadata.purchase_date }),
          ...(receiptMetadata?.store_name && { purchaseStore: receiptMetadata.store_name }),
        };
      });

      // Last line of defence against duplicates. The capture review only
      // pre-unchecks what it recognises, so without this a food that is already
      // in the pantry under another preparation ("nectarines" vs "nectarines,
      // tranchées") lands as a second row.
      const deduped: InventoryItem[] = [];
      for (const candidate of newItems) {
        const against = [...state.items, ...deduped];
        if (findDuplicate(candidate.name, against)) continue;
        deduped.push(candidate);
      }

      if (deduped.length === 0) return;

      state.items.push(...deduped);
      state.lastUpdated = now;
    },

    /**
     * Put portions of a cooked dish in the freezer (#663).
     *
     * Deliberately NOT routed through `addItemsToPantry`: that runs every name
     * through the ingredient pipeline (`cleanPantryName` / category inference),
     * which is built for shopping-list lines. "Pad thaï au poulet" is a dish,
     * not an ingredient, and normalising it would mangle the name and file it
     * under a guessed food category. The row is built explicitly instead.
     */
    addLeftoverToFreezer: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        recipeId?: string;
        portions: number;
        cookedAt: string;
        expiryDate?: PantryExpiryDate;
      }>
    ) => {
      const { id, name, recipeId, portions, cookedAt, expiryDate } = action.payload;
      if (portions <= 0) return;
      const now = new Date().toISOString();

      state.items.push({
        id,
        name,
        category: LEFTOVER_CATEGORY,
        location: 'freezer',
        quantity: portions,
        unit: 'portions',
        dateAdded: now,
        lastModified: now,
        status: 'active',
        source: 'leftover',
        ...(recipeId ? { leftoverRecipeId: recipeId } : {}),
        leftoverCookedAt: cookedAt,
        ...(expiryDate ? { expiryDate } : {}),
      });
      state.lastUpdated = now;
    },

    /**
     * Take one serving of a frozen leftover out for a meal.
     *
     * Marked eaten at zero rather than deleted, so the waste metrics keep the
     * same consumption history they get for every other pantry row.
     */
    consumeLeftoverPortion: (
      state,
      action: PayloadAction<{ id: string; portions?: number }>
    ) => {
      const { id, portions = 1 } = action.payload;
      const item = state.items.find((i) => i.id === id);
      if (!item) return;

      const now = new Date().toISOString();
      const left = Math.max(0, (item.quantity ?? 0) - portions);
      item.quantity = left;
      item.lastModified = now;

      if (left === 0) {
        item.status = 'eaten';
        item.eatenAt = now;
        item.consumedBeforeExpiry = item.expiryDate
          ? new Date() < new Date(item.expiryDate.year, item.expiryDate.month - 1, item.expiryDate.day)
          : undefined;
      }
      state.lastUpdated = now;
    },

    /**
     * Retire the import banner for one delivery.
     *
     * lastModified is bumped on purpose: the cloud merge resolves conflicts by
     * timestamp, so without it the next sync would hand back the unreviewed
     * copy written by the server and the banner would come straight back.
     */
    markImportReviewed: (state, action: PayloadAction<string>) => {
      const importId = action.payload;
      const now = new Date().toISOString();
      let touched = false;

      state.items.forEach((item) => {
        if (item?.importId === importId && item.reviewed === false) {
          item.reviewed = true;
          item.lastModified = now;
          touched = true;
        }
      });

      if (touched) state.lastUpdated = now;
    },

    updateItem: (state, action: PayloadAction<{ id: string; updates: Partial<InventoryItem> }>) => {
      const { id, updates } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === id);

      if (itemIndex !== -1) {
        const now = new Date().toISOString();
        state.items[itemIndex] = {
          ...state.items[itemIndex],
          ...updates,
          lastModified: now, // Track modification time for sync
          isExpiringSoon: updates.expiryDate
            ? isExpiringSoon(updates.expiryDate)
            : state.items[itemIndex].isExpiringSoon
        };
        state.lastUpdated = now;
      }
    },
    
    removeItem: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const index = state.items.findIndex(item => item.id === itemId);
      if (index !== -1) {
        state.items.splice(index, 1);
      }
      state.lastUpdated = new Date().toISOString();
      // Note: Cloud sync happens in thunk below
    },
    
    clearInventory: (state) => {
      // Keeps eaten and discarded rows, matching clearCloudInventory. The two
      // halves have to agree: if local dropped history and the cloud kept it,
      // the next sync would pull it all back and the clear would look broken.
      //
      // Cooked leftovers are kept for the same reason (#663): clearing the
      // pantry means the ingredients got out of hand, not "bin the meals I
      // cooked and froze". A shop-bought prepared meal is a product and does
      // get cleared, which is why this keys on `source`, not the category.
      // clearCloudInventory spares exactly the same rows.
      state.items = state.items.filter(
        (item) =>
          item.status === 'eaten' ||
          item.status === 'discarded' ||
          isCookedLeftover(item)
      );
      state.lastUpdated = new Date().toISOString();
      state.lastCleared = new Date().toISOString();
    },
    
    refreshExpiryStatus: (state) => {
      // Update expiry status for all items
      state.items.forEach(item => {
        if (item.expiryDate) {
          item.isExpiringSoon = isExpiringSoon(item.expiryDate);
        }
      });
      state.lastUpdated = new Date().toISOString();
    },

    // Mark item as eaten
    markAsEaten: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const item = state.items.find(i => i.id === itemId);

      if (item) {
        const now = new Date();
        const nowISO = now.toISOString();
        const wasConsumedBeforeExpiry = item.expiryDate
          ? now < new Date(item.expiryDate.year, item.expiryDate.month - 1, item.expiryDate.day)
          : undefined;

        item.status = 'eaten';
        item.eatenAt = nowISO;
        item.lastModified = nowISO; // Track modification time for sync
        item.consumedBeforeExpiry = wasConsumedBeforeExpiry;
        state.lastUpdated = nowISO;
      }
    },

    // Mark multiple items as eaten (for bulk actions)
    markMultipleAsEaten: (state, action: PayloadAction<string[]>) => {
      const itemIds = action.payload;
      const now = new Date();
      const nowISO = now.toISOString();

      itemIds.forEach(itemId => {
        const item = state.items.find(i => i.id === itemId);

        if (item) {
          const wasConsumedBeforeExpiry = item.expiryDate
            ? now < new Date(item.expiryDate.year, item.expiryDate.month - 1, item.expiryDate.day)
            : undefined;

          item.status = 'eaten';
          item.eatenAt = nowISO;
          item.lastModified = nowISO; // Track modification time for sync
          item.consumedBeforeExpiry = wasConsumedBeforeExpiry;
        }
      });

      state.lastUpdated = nowISO;
    },

    // Set items from cloud (for real-time updates)
    setItems: (state, action: PayloadAction<InventoryItem[]>) => {
      state.items = action.payload.map(migrateItemStatus).filter(isValidInventoryItem).map(withLocation);
      state.lastUpdated = new Date().toISOString();
    },

    // Clear sync error
    clearSyncError: (state) => {
      if (!state.sync) {
        state.sync = { isSyncing: false, lastSyncTime: null, error: null };
      }
      state.sync.error = null;
    },

    // Sync items from cloud (real-time listener)
    syncFromCloud: (state, action: PayloadAction<InventoryItem[]>) => {
      const incomingItems = action.payload.map(migrateItemStatus).filter(isValidInventoryItem).map(withLocation);

      // Skip update if both are empty
      if (state.items.length === 0 && incomingItems.length === 0) {
        return;
      }

      // Skip update if items are identical (compare IDs + status to catch eaten/active changes)
      if (state.items.length === incomingItems.length) {
        const fingerprint = (items: InventoryItem[]) =>
          items.map(i => `${i.id}:${i.status || 'active'}`).sort().join(',');
        if (fingerprint(state.items) === fingerprint(incomingItems)) {
          return;
        }
      }

      // Grace period: don't let cloud restore items that were recently
      // modified locally (covers both clearInventory and individual removes/eats).
      // This prevents the race condition where cloud hasn't caught up with
      // local bulk operations, which causes rapid state oscillation.
      if (state.lastUpdated) {
        const lastUpdate = new Date(state.lastUpdated).getTime();
        const now = Date.now();
        const GRACE_PERIOD_MS = 10000; // 10 seconds

        if (now - lastUpdate < GRACE_PERIOD_MS) {
          // During grace period, never restore items into an empty local state
          if (state.items.length === 0 && incomingItems.length > 0) {
            console.log('⏳ Skipping cloud sync — local state was recently emptied');
            return;
          }

          // During grace period, don't let cloud overwrite local changes
          // (cloud may have stale items that were locally eaten/deleted/added)
          const localActiveCount = state.items.filter(
            i => !i.status || i.status === 'active'
          ).length;
          const incomingActiveCount = incomingItems.filter(
            i => !i.status || i.status === 'active'
          ).length;
          if (incomingActiveCount > localActiveCount) {
            console.log('⏳ Skipping cloud sync — would restore locally-removed items');
            return;
          }
          if (incomingActiveCount < localActiveCount) {
            console.log('⏳ Skipping cloud sync — cloud doesn\'t have locally-added items yet');
            return;
          }
        }
      }

      console.log(`📥 Syncing ${action.payload.length} items from cloud to local state`);

      // Replace local items with cloud items (cloud is source of truth)
      state.items = incomingItems;
      state.lastUpdated = new Date().toISOString();

      if (!state.sync) {
        state.sync = { isSyncing: false, lastSyncTime: null, error: null };
      }
      state.sync.lastSyncTime = new Date().toISOString();
      state.sync.error = null;

      console.log(`✅ Local state updated with ${state.items.length} items from cloud`);
    },

    /**
     * Silently add ingredients to pantry if they don't already exist.
     * Used by Prep Check, IngredientReviewSheet, and grocery list check-off.
     * Skips items that match existing active inventory (exact, fuzzy, or word-boundary).
     */
    addItemsToPantry: (
      state,
      action: PayloadAction<{
        ingredientNames: string[];
        /**
         * Zones the user has already chosen for these foods, keyed by
         * `primaryKey(name)`. Passed in by the thunk because a reducer cannot
         * reach another slice, and a learned placement has to beat the rules.
         */
        learnedZones?: Record<string, PantryLocation>;
        /** Basiques the cook turned off: shelved like any other food. */
        excludedStaples?: readonly string[];
      }>
    ) => {
      const { ingredientNames, learnedZones, excludedStaples } = action.payload;
      if (ingredientNames.length === 0) return;

      const now = new Date().toISOString();
      let added = false;

      for (const raw of ingredientNames) {
        // Not a food, a staple, or already shelved — see decidePantryAdd. The
        // line is reduced to the food it names first: this reducer used to
        // store the line verbatim, which is why the pantry accumulated six
        // separate rows of lime juice.
        const decision = decidePantryAdd(raw, state.items, excludedStaples);
        if (decision.kind === 'skip') continue;

        const name = decision.name;

        const detected = detectCategoryConfident(name);
        const category = detected.category;
        const zone = inferLocationConfident(
          category,
          name,
          detected.confident,
          learnedZones?.[primaryKey(name)]
        );

        // Was a three-line category table that gave every vegetable five days
        // and every condiment ninety. Now the same service every other add path
        // uses, so "I have chicken" and a scanned chicken agree.
        const estimatedExpiry = shelfLifeExpiryDate(
          { name, category, location: zone.location },
          new Date(),
          normalizeIngredientName(name)
        );

        const canonicalId = specificIdOf(raw, name);
        const newItem: InventoryItem = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name,
          ...(canonicalId ? { canonicalId } : {}),
          category,
          dateAdded: now,
          lastModified: now,
          status: 'active',
          expiryDate: estimatedExpiry,
          isExpiringSoon: estimatedExpiry ? isExpiringSoon(estimatedExpiry) : false,
          location: zone.location,
          ...(zone.confident ? {} : { needsSorting: true }),
        };

        state.items.push(newItem);
        added = true;
      }

      if (added) {
        state.lastUpdated = now;
      }
    },

    /**
     * Collapse groups of duplicate rows into one row each.
     * Payload carries the item ids per group so the reducer merges exactly what
     * the user saw in the preview, not whatever re-detection would find now.
     */
    mergeDuplicates: (state, action: PayloadAction<{ groups: string[][] }>) => {
      const { groups } = action.payload;
      const now = new Date().toISOString();
      let merged = false;

      for (const ids of groups) {
        const members = ids
          .map(id => state.items.find(item => item.id === id))
          .filter((item): item is InventoryItem => !!item);

        if (members.length < 2) continue;

        const { survivor, absorbed, updates } = planDuplicateMerge(members);
        const removedIds = new Set(absorbed.map(item => item.id));

        const survivorIndex = state.items.findIndex(item => item.id === survivor.id);
        if (survivorIndex !== -1) {
          state.items[survivorIndex] = {
            ...state.items[survivorIndex],
            ...updates,
            lastModified: now,
          };
        }

        state.items = state.items.filter(item => !removedIds.has(item.id));
        merged = true;
      }

      if (merged) {
        state.lastUpdated = now;
      }
    },

    /**
     * Add item from grocery list
     * Used when user clicks "I Already Have This" on a grocery item
     */
    addFromGroceryList: (
      state,
      action: PayloadAction<{
        name: string;
        category: string;
        quantity?: number;
        unit?: string; // Phase 4: Support units (cups, g, ml, tablespoons)
        expiryDate?: {
          day: number;
          month: number;
          year: number;
          confidence: number;
          explanation: string;
        };
      }>
    ) => {
      const { name, category, quantity, unit, expiryDate } = action.payload;
      const now = new Date().toISOString();

      const newItem: InventoryItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name,
        category,
        dateAdded: now,
        lastModified: now,
        status: 'active',
        source: 'fridge', // Could be 'grocery-list' if we add that option
        location: inferLocation(category, name),
        ...(quantity && { quantity }),
        ...(unit && { unit }), // Phase 4: Store unit if provided
        ...(expiryDate && {
          expiryDate,
          isExpiringSoon: isExpiringSoon(expiryDate),
        }),
      };

      state.items.push(newItem);
      state.lastUpdated = now;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload to cloud
      .addCase(uploadInventoryToCloud.pending, (state) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = true;
        state.sync.error = null;
      })
      .addCase(uploadInventoryToCloud.fulfilled, (state, action) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = false;
        state.sync.lastSyncTime = action.payload.syncTime;
      })
      .addCase(uploadInventoryToCloud.rejected, (state, action) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = false;
        state.sync.error = action.payload as string;
      })
      // Download from cloud
      .addCase(downloadInventoryFromCloud.pending, (state) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = true;
        state.sync.error = null;
      })
      .addCase(downloadInventoryFromCloud.fulfilled, (state, action) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = false;

        // Don't restore cloud items if inventory was recently cleared
        if (state.lastCleared) {
          const clearedAt = new Date(state.lastCleared).getTime();
          const now = Date.now();
          if (now - clearedAt < 30000 && state.items.length === 0 && action.payload.items.length > 0) {
            console.log('⏳ Skipping cloud download — inventory was recently cleared');
            state.sync.lastSyncTime = action.payload.syncTime;
            return;
          }
        }

        state.items = action.payload.items.map(migrateItemStatus);
        state.sync.lastSyncTime = action.payload.syncTime;
        state.lastUpdated = action.payload.syncTime;
      })
      .addCase(downloadInventoryFromCloud.rejected, (state, action) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = false;
        state.sync.error = action.payload as string;
      })
      // Full sync
      .addCase(fullSync.pending, (state) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = true;
        state.sync.error = null;
      })
      .addCase(fullSync.fulfilled, (state, action) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = false;
        state.sync.lastSyncTime = action.payload.syncTime;
      })
      .addCase(fullSync.rejected, (state, action) => {
        if (!state.sync) {
          state.sync = { isSyncing: false, lastSyncTime: null, error: null };
        }
        state.sync.isSyncing = false;
        state.sync.error = action.payload as string;
      });
  },
});

// Helper function to check if an item is expiring soon (within 7 days)
function isExpiringSoon(expiryDate: { day: number; month: number; year: number }): boolean {
  const today = new Date();
  const expiry = new Date(expiryDate.year, expiryDate.month - 1, expiryDate.day);
  const diffInDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diffInDays <= 7 && diffInDays >= 0;
}

export const {
  addItems,
  addItemsToPantry,
  addLeftoverToFreezer,
  consumeLeftoverPortion,
  updateItem,
  removeItem,
  clearInventory,
  refreshExpiryStatus,
  markAsEaten,
  markMultipleAsEaten,
  setItems,
  clearSyncError,
  syncFromCloud,
  addFromGroceryList,
  mergeDuplicates,
  markImportReviewed,
} = inventorySlice.actions;

// Base selector for all inventory items
// Accepts any state shape that has an inventory property
const selectInventoryState = (state: any) => state.inventory as InventoryState;

export const selectAllInventory = createSelector(
  [selectInventoryState],
  (inventory) => inventory.items
);

// Memoized selector for active inventory items only
// This prevents unnecessary rerenders by caching the filtered result
export const selectActiveInventory = createSelector(
  [selectAllInventory],
  (items) => items.filter(item => !item.status || item.status === 'active')
);

/**
 * Lower-cased names of the active pantry rows, for the recipes screen's
 * "Mon frigo" chip and pantry shelf.
 *
 * #668: the screen used to derive this from EVERY row, eaten and discarded
 * included — 805 names instead of 232 on the reporting account, and a recipe
 * could read as "in my fridge" because of something eaten weeks ago.
 */
export const selectActivePantryNames = createSelector(
  [selectActiveInventory],
  (items) => items.map(item => item.name.toLowerCase())
);

export default inventorySlice.reducer;